// src/pages/group/view/Group_list.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Visibility, Edit, Delete, Search } from "@mui/icons-material";
import axios from "axios";
import { ENDPOINT } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const GroupList = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // ---------------- Fetch Groups ----------------
  const fetchGroups = async (url = `${ENDPOINT.GROUP_LIST}?page=1`, searchQuery = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const fullUrl = searchQuery
        ? `${ENDPOINT.GROUP_LIST}?search=${encodeURIComponent(searchQuery)}`
        : url;

      const response = await axios.get(fullUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;

      // 🔹 Handle page overflow
      const pageMatch = fullUrl.match(/page=(\d+)/);
      let page = pageMatch ? parseInt(pageMatch[1]) : 1;
      if (page > data.total_pages && data.total_pages > 0) {
        return fetchGroups(`${ENDPOINT.GROUP_LIST}?page=${data.total_pages}`, searchQuery);
      }

      setGroups(data.results || []);
      setNext(data.next);
      setPrevious(data.previous);
      setCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching groups:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        localStorage.clear();
        window.location.href = "/login";
      } else {
        toast.error("Failed to fetch groups");
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------- On Mount ----------------
  useEffect(() => {
    fetchGroups();

    if (location.state?.toastMessage) {
      toast.success(location.state.toastMessage);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // ---------------- Handlers ----------------
  const handleSearch = (e) => {
    e.preventDefault();
    fetchGroups(`${ENDPOINT.GROUP_LIST}?page=1`, search);
  };

  const handleNext = () => next && fetchGroups(next, search);
  const handlePrevious = () => previous && fetchGroups(previous, search);

  const handleDetail = (id) => navigate(`/groups/${id}`);
  const handleEdit = (id) => navigate(`/groups/update/${id}`);

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const closeDeleteDialog = () => {
    setDeleteId(null);
    setOpenDialog(false);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(ENDPOINT.GROUP_DELETE(deleteId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Group deleted successfully!");

      // 🔹 If last group on page, go to previous page if exists
      if (groups.length === 1 && currentPage > 1) {
        fetchGroups(`${ENDPOINT.GROUP_LIST}?page=${currentPage - 1}`, search);
      } else {
        fetchGroups(`${ENDPOINT.GROUP_LIST}?page=${currentPage}`, search);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete group");
    } finally {
      closeDeleteDialog();
    }
  };

  // ---------------- UI ----------------
  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        {/* 🔍 Search Box */}
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            size="small"
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
          <Button type="submit" variant="contained" color="primary" sx={{ ml: 1 }} disabled={loading}>
            Search
          </Button>
        </Box>

        {/* ➕ Create Group Button */}
        <Button
          variant="contained"
          color="success"
          sx={{ ml: 2, textTransform: "none" }}
          onClick={() => navigate("/groups/create")}
        >
          + Create Group
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {groups.length > 0 ? (
                  groups.map((group) => (
                    <TableRow key={group.id} hover>
                      <TableCell>{group.id}</TableCell>
                      <TableCell>
                        <Button onClick={() => handleDetail(group.id)} variant="text" sx={{ textTransform: "none" }}>
                          {group.name}
                        </Button>
                      </TableCell>

                      <TableCell>
                        {group.permissions?.length > 0 ? (
                          <Tooltip title={group.permissions.join(", ")} arrow placement="top">
                            <Chip label={`${group.permissions.length} perms`} color="secondary" size="small" />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No Permissions
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="View Detail">
                            <IconButton color="primary" onClick={() => handleDetail(group.id)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Group">
                            <IconButton color="warning" onClick={() => handleEdit(group.id)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Group">
                            <IconButton color="error" onClick={() => openDeleteDialog(group.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No groups found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <Stack direction="row" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: "#fafafa" }}>
            <Typography variant="body2" color="text.secondary">
              Total Groups: <strong>{count}</strong> | Total Pages: <strong>{totalPages}</strong>
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" size="small" disabled={!previous} onClick={handlePrevious}>
                ← Prev
              </Button>
              <Typography variant="body2">
                Page {currentPage} / {totalPages}
              </Typography>
              <Button variant="outlined" size="small" disabled={!next} onClick={handleNext}>
                Next →
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this group? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GroupList;